import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { addToastNotification } from 'redux/actions';

import { presetPolygonColors, Status } from 'common/utils/constants';
import { getDefaultPolygonColor } from '../../polygonUtils';
import { POLYGON_OPTIONS_MODAL_MODE } from 'common/utils/constants';

const PolygonCreationPopup = (props) => {
  const {
    newPolygonName,
    newPolygonColor,
    polygonOptionsModal: { mode, selectedPolygon },
    savePolygon,
    closePolygonOptionsModal
  } = props;
  const dispatch = useDispatch();
  const pendingPolygonCreate = useSelector((state) => state.statuses['POST-User-DevicePolygon'] === Status.PENDING);

  const [newPolygonSettings, setNewPolygonSettings] = useState({
    name: mode === POLYGON_OPTIONS_MODAL_MODE.CREATION ? newPolygonName : selectedPolygon.properties.name,
    fill:
      mode === POLYGON_OPTIONS_MODAL_MODE.CREATION
        ? newPolygonColor
        : mode === POLYGON_OPTIONS_MODAL_MODE.DUPLICATION
          ? getDefaultPolygonColor(selectedPolygon.properties.fill)
          : selectedPolygon.properties.fill,
    default: mode === POLYGON_OPTIONS_MODAL_MODE.UPDATION ? selectedPolygon.properties.default : false
  });

  const [clickedKeyCode, setClickedKeyCode] = useState(null);

  const handleKeyDown = (e) => {
    setClickedKeyCode(e.keyCode);
  };

  const checkPolygonNameIsEmpty = useCallback(() => {
    if (newPolygonSettings.name.trim() === '') {
      dispatch(
        addToastNotification({
          id: 'test',
          title: 'Error',
          description: 'Polygon name is required'
        })
      );
      return true;
    }
    return false;
  }, [dispatch, newPolygonSettings.name]);

  // Create or update polygon
  const handlePolygonSave = useCallback(() => {
    const isNameEmpty = checkPolygonNameIsEmpty();

    if (!isNameEmpty) {
      savePolygon(mode, newPolygonSettings, selectedPolygon);
    }
  }, [checkPolygonNameIsEmpty, mode, newPolygonSettings, savePolygon, selectedPolygon]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown, false);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, false);
    };
  }, []);

  useEffect(() => {
    // Cancel
    if (clickedKeyCode === 27) {
      closePolygonOptionsModal();
    }

    // Enter
    if (clickedKeyCode === 13) {
      handlePolygonSave();
    }
  }, [clickedKeyCode, closePolygonOptionsModal, handlePolygonSave]);

  const handleChangePolygonSetting = (settingName, value) => {
    setNewPolygonSettings({
      ...newPolygonSettings,
      [settingName]: value
    });
  };

  return (
    <>
      <div className="DeviceMapOverlay_CenterDiv">
        <div>
          <input
            type="text"
            onChange={(e) => handleChangePolygonSetting('name', e.target.value)}
            value={newPolygonSettings.name}
            className="DeviceMapOverlay_PolygonNameField"
          />
        </div>
        <div className="DeviceMapOverlay_SavePolygonColorControl">
          {presetPolygonColors.map((color) => (
            <div
              key={color}
              className={classNames({
                DeviceMapOverlay_SavePolygonColorControl_Item: true,
                DeviceMapOverlay_SavePolygonColorControl_Item_Selected: newPolygonSettings.fill === color
              })}
              style={{ backgroundColor: color }}
              onClick={() => handleChangePolygonSetting('fill', color)}
            />
          ))}
        </div>
        <div className="DeviceMapOverlay_SavePolygon_Checkbox_Container">
          <input
            type="checkbox"
            checked={newPolygonSettings.default}
            onChange={(e) => handleChangePolygonSetting('default', e.target.checked)}
            id="default"
          />
          <label htmlFor="default" className="DeviceMapOverlay_SavePolygon_Checkbox_Label">
            Default polygon
          </label>
        </div>
        <div className="DeviceMapOverlay_SavePolygonControl">
          {
            // A polygon is being edited
            mode === POLYGON_OPTIONS_MODAL_MODE.UPDATION && (
              <>
                <div className="DeviceMapOverlay_SavePolygonControl_Button_Cancel" onClick={closePolygonOptionsModal}>
                  Cancel
                </div>
                <div className="DeviceMapOverlay_SavePolygonControl_Button_Save" onClick={handlePolygonSave}>
                  Save
                </div>
              </>
            )
          }

          {
            // New polygon is being created
            mode !== POLYGON_OPTIONS_MODAL_MODE.UPDATION && (
              <>
                <div className="DeviceMapOverlay_SavePolygonControl_Button_Cancel" onClick={closePolygonOptionsModal}>
                  Cancel
                </div>
                <div
                  className={classNames({
                    DeviceMapOverlay_SavePolygonControl_Button_Save: true,
                    DeviceMapOverlay_SavePolygonControl_Button_Disabled: pendingPolygonCreate
                  })}
                  onClick={handlePolygonSave}
                >
                  Save
                </div>
              </>
            )
          }
        </div>
      </div>
    </>
  );
};

export default PolygonCreationPopup;
